import log from '../utils/logger';
/**
 * Runtime Configuration Module
 * Fetches configuration from the backend /config endpoint at runtime
 * Falls back to environment variables for local development
 */

let config = null;

/**
 * Fetches configuration from the backend
 * @returns {Promise<Object>} Configuration object
 */
export const fetchConfig = async () => {
  if (config) {
    return config;
  }
  
  try {
    // Try to fetch from backend
    const configUrl = process.env.REACT_APP_CONFIG_URL || `${window.location.origin}/config`;
    
    const response = await fetch(configUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      // Don't send credentials to avoid CORS issues
      credentials: 'omit'
    });
    
    if (!response.ok) {
      throw new Error(`Config fetch failed: ${response.status} ${response.statusText}`);
    }
    
    config = await response.json();
    
    return config;
  } catch (error) {
    log.warn('Failed to load config from backend, using environment variables');
    
    // Fallback to build-time env vars for local development
    config = {
      apiUrl: process.env.REACT_APP_API_URL || 'http://localhost:5000',
      auth0Domain: process.env.REACT_APP_AUTH0_DOMAIN,
      auth0ClientId: process.env.REACT_APP_AUTH0_CLIENT_ID,
      auth0RedirectUri: process.env.REACT_APP_AUTH0_REDIRECT_URI || window.location.origin,
      auth0Audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      stripePublishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
    };
    
    return config;
  }
};

/**
 * Gets the cached configuration
 * @returns {Object} Configuration object
 * @throws {Error} If config hasn't been initialized
 */
export const getConfig = () => {
  if (!config) {
    throw new Error('Config not initialized. Call fetchConfig() first.');
  }
  return config;
};

/**
 * Resets the configuration cache (useful for testing)
 */
export const resetConfig = () => {
  config = null;
};
